/**
 * The rules a practitioner's link name has to keep.
 *
 * The name becomes a hostname — `diksha-kumari.naurist.com` — so these are not
 * house style. A hostname label is lower-case, letters, digits and hyphens
 * only, no hyphen at either end, and at most 63 characters; anything else is
 * not a name the DNS can carry, and the browser would refuse the address
 * before the server ever saw it.
 *
 * Kept apart from the input and from the service so both read the same rules:
 * the box normalises what is typed against them, and the save is checked
 * against them before anything goes on the wire.
 */

/**
 * Names that must never be handed out, because something else already answers
 * on them.
 *
 * Three kinds, and losing any of them costs more than a broken form:
 * `app` and `www` are the dashboard and the marketing site; `mail`, `smtp`,
 * `imap`, `webmail` and `autodiscover` are how mail is delivered and how a
 * mail client finds the server; `ns1`, `ns2`, `cpanel` and `ftp` are how the
 * hosting is administered. Giving `mail` to a dietitian does not break a page,
 * it breaks the company's email.
 *
 * `form` and `forms` are here for a fourth reason: they are the path the
 * enquiry form answers on, so on the shared host `connect.naurist.com/form`
 * would be read as that route rather than as somebody's name, and the owner of
 * the name would be the one who could not be reached. See `FORM_PATHS`.
 *
 * The backend has to keep this list too — this copy only saves a round trip
 * for a name that was never going to be allowed.
 */
export const RESERVED_LINK_NAMES: ReadonlySet<string> = new Set([
  "admin",
  "api",
  "app",
  "autodiscover",
  "blog",
  "connect",
  "cpanel",
  "dashboard",
  "dev",
  "form",
  "forms",
  "ftp",
  "help",
  "imap",
  "mail",
  "naurist",
  "ns1",
  "ns2",
  "smtp",
  "staging",
  "static",
  "support",
  "test",
  "webmail",
  "www",
]);

export const LINK_NAME_MIN = 3;
/**
 * Well under the DNS label ceiling of 63. The limit here is a link somebody
 * has to read out, type from a story, or fit in an Instagram bio — not what
 * the protocol allows.
 */
export const LINK_NAME_MAX = 30;

/**
 * What a keystroke leaves in the box.
 *
 * Corrects rather than rejects: a space becomes a hyphen and a capital becomes
 * lower case, because that is what the person meant, and an error message
 * saying "no capitals" under a field that let them type one is a worse way to
 * say it. Characters with no sensible reading — a slash, an accent, an emoji —
 * are dropped.
 *
 * Runs of hyphens are collapsed, but a trailing one survives: it is how a name
 * gets typed on the way to `diksha-kumari`, and eating it as it is pressed
 * makes the field feel broken. `nameError` is what refuses to save it.
 */
export const normaliseLinkName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s_.]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "")
    .slice(0, LINK_NAME_MAX);

/**
 * Why this name cannot be used, or `undefined` if it can.
 *
 * Says only what the app can know on its own. Whether somebody else already
 * has the name is the server's answer, and it arrives when the name is
 * claimed — see `saveFormLink`.
 */
export const nameError = (value: string): string | undefined => {
  const name = value.trim();

  if (!name) return "Choose a name for your link";
  if (name.length < LINK_NAME_MIN)
    return `Use at least ${LINK_NAME_MIN} characters`;
  if (name.length > LINK_NAME_MAX)
    return `Use at most ${LINK_NAME_MAX} characters`;
  if (!/^[a-z0-9-]+$/.test(name))
    return "Use lowercase letters, numbers and hyphens only";
  if (name.startsWith("-") || name.endsWith("-"))
    return "Names cannot start or end with a hyphen";
  if (name.includes("--"))
    return "Use one hyphen at a time";
  if (RESERVED_LINK_NAMES.has(name))
    return `${name} is reserved - please choose another name`;

  return undefined;
};
